"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  Flag,
  Globe2,
  Heart,
  Info,
  Image as Photo,
  Mail,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Reply,
  Share2,
  ShieldCheck,
  Star,
  Tag,
  X,
} from "lucide-react";
import useAuthInfo from "@/store/auth";
import Swal from "sweetalert2";
import { AjaxGet, AjaxPost, AjaxPostAnon } from "@/util/Ajax";
import { type DiscoverService, searchHref, serviceHref, webUrl, whatsappUrl } from "@/lib/discover";
import type { DiscoverCompany } from "./discover-detail-client";
import styles from "./discover-profile.module.css";

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const money = (value: number, currency = "ZAR") =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
const dateLabel = (value: string) =>
  new Intl.DateTimeFormat("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
const initials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
const responseMessage = (response: any, fallback: string) =>
  response?.response?.data?.message ||
  response?.response?.data?.errors?.[0] ||
  response?.message ||
  fallback;

function Stars({ value, label }: { value: number; label?: string }) {
  return (
    <span
      className={styles.stars}
      aria-label={label || `${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={16}
          fill={star <= Math.round(value) ? "currentColor" : "none"}
        />
      ))}
    </span>
  );
}

export default function DiscoverBusinessProfile({
  company,
}: {
  company: DiscoverCompany;
}) {
  const apiToken = useAuthInfo((state) => state.apiToken);
  const [isOwner, setIsOwner] = useState(false);
  const [saved, setSaved] = useState(false);
  const [followingBusy, setFollowingBusy] = useState(false);
  const [shareStatus, setShareStatus] = useState("");
  const [activeImage, setActiveImage] = useState<number | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [bookingStatus, setBookingStatus] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const viewed = useRef("");
  const usedContacts = useRef(new Set<string>());

  const gallery = (company.gallery || [])
    .map(webUrl)
    .filter((url): url is string => Boolean(url));
  const socials = company.hasActiveSubscription
    ? (company.socials || []).filter((item) => webUrl(item.url))
    : [];
  const hours = [...(company.tradingHours || [])].sort(
    (a, b) => ((a.dayOfWeek + 6) % 7) - ((b.dayOfWeek + 6) % 7),
  );
  const services = company.services || [];
  // Products are shown in their own section and are never bookable.
  const serviceOfferings = services.filter((service) => !service.isProduct);
  const productOfferings = services.filter((service) => service.isProduct);
  // Only services flagged IsBooking can be requested through the booking form.
  const bookableServices = serviceOfferings.filter((service) => service.isBooking !== false);
  const specials = company.specials || [];
  const reviews = company.reviews || [];
  const verificationTags = company.verificationTags || [];
  const summary = company.reviewSummary || {
    averageRating: company.averageRating || 0,
    reviewCount: company.reviewCount || 0,
    distribution: {},
  };
  const primaryLocation = company.locations?.[0];
  const addressText = primaryLocation
    ? [
        primaryLocation.streetAddress,
        primaryLocation.city,
        primaryLocation.province,
        primaryLocation.postalCode,
        primaryLocation.country,
      ]
        .filter(Boolean)
        .join(", ")
    : company.location || "";
  const mapUrl = addressText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText)}`
    : undefined;
  const todayHours = hours.find(
    (hour) => hour.dayOfWeek === new Date().getDay(),
  );
  const contactItems = company.hasActiveSubscription
    ? [
        {
          label: "Email",
          eyebrow: "Business email",
          value: company.email,
          href: company.email
            ? `mailto:${company.email.replace(/[\r\n]/g, "")}`
            : undefined,
          icon: Mail,
        },
        {
          label: "Phone",
          eyebrow: "Direct phone",
          value: company.phone,
          href: company.phone
            ? `tel:${company.phone.replace(/[^\d+]/g, "")}`
            : undefined,
          icon: Phone,
        },
        {
          label: "WhatsApp",
          eyebrow: "Direct WhatsApp",
          value: company.whatsapp ? "Chat on WhatsApp" : undefined,
          href: whatsappUrl(company.whatsapp),
          icon: MessageCircle,
        },
        {
          label: "Website",
          eyebrow: "Official website",
          value: company.website,
          href: webUrl(company.website),
          icon: Globe2,
        },
      ].filter((item) => item.value && item.href)
    : [];

  const track = (eventType: string) =>
    void AjaxPostAnon("Discover/Analytics", {
      encListingId: company.encryptedId,
      eventType,
      referrerUrl: window.location.href,
    });
  useEffect(() => {
    void useAuthInfo.persist.rehydrate();
  }, []);
  useEffect(() => {
    let current = true;
    if (apiToken)
      void AjaxGet({ apiToken }, `Discover/Owner/${company.encryptedId}`).then(
        (response) => {
          if (current) setIsOwner(Boolean(response?.data?.isOwner));
        },
      );
    return () => {
      current = false;
    };
  }, [apiToken, company.encryptedId]);
  useEffect(() => {
    if (viewed.current !== company.encryptedId) {
      viewed.current = company.encryptedId;
      track("View");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.encryptedId]);
  useEffect(() => {
    if (activeImage === null && !bookingOpen && !reviewOpen) return;
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = old;
    };
  }, [activeImage, bookingOpen, reviewOpen]);

  useEffect(() => {
    let active = true;
    setSaved(false);
    if (apiToken) void AjaxGet({ apiToken }, `Discover/Follow/${company.encryptedId}`).then(res => { if (active) setSaved(Boolean(res?.data?.following)); });
    return () => { active = false; };
  }, [apiToken, company.encryptedId]);
  const follow = async () => {
    if (!apiToken) { await Swal.fire({ icon: "info", title: "Sign in to follow", text: "Sign in to follow businesses and receive email updates.", confirmButtonText: "Sign in" }); window.location.href = "/login"; return; }
    if (!saved) {
      const consent = await Swal.fire({ icon: "info", title: `Follow ${company.title}?`, text: "Your name and email address will be shared with this business. You will receive email updates about services, products, promotions, discounts, sales and specials. You can unfollow at any time.", showCancelButton: true, confirmButtonText: "Follow", cancelButtonText: "Cancel" });
      if (!consent.isConfirmed) return;
    }
    setFollowingBusy(true);
    const res = await AjaxPost({ apiToken }, "Discover/Follow", { encListingId: company.encryptedId, follow: !saved, consent: !saved });
    setFollowingBusy(false);
    if (typeof res?.data?.following === "boolean") setSaved(res.data.following);
    else await Swal.fire({ icon: "error", title: "Could not update follow", text: responseMessage(res, "Please try again.") });
  };
  // Card for a service or product, styled like the business cards on the Discover home page.
  const renderOffering = (service: DiscoverService, index: number) => {
    const href = serviceHref(company, service);
    const isProduct = Boolean(service.isProduct);
    // Its own cover first, then the listing's cover photo, then the logo.
    const thumb =
      webUrl(service.photos?.[0] || undefined) || gallery[0] || webUrl(company.imageUrl);
    const FallbackIcon = isProduct ? Package : BriefcaseBusiness;
    const tag = isProduct
      ? "Product"
      : service.isBooking !== false
        ? "Bookable online"
        : "Service";
    const meta = [
      !isProduct && service.durationMinutes ? `${service.durationMinutes} min` : null,
      company.isRemote ? "Remote" : null,
    ].filter(Boolean);
    return (
      <Link
        key={service.encryptedId || `${service.name}-${index}`}
        href={href}
        className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:border-[#0d5c75]/40 hover:shadow-lg"
      >
        <div className="relative flex h-44 items-center justify-center bg-[#eef6f5]">
          {thumb ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={thumb} alt="" loading="lazy" className="h-full max-w-full object-contain" />
          ) : (
            <FallbackIcon size={40} className="text-[#0d5c75]/60" aria-hidden="true" />
          )}
          <span className="absolute left-3 top-3 rounded-lg bg-white px-3 py-1 text-xs font-bold text-[#0d5c75] shadow-sm">
            {tag}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h4 className="text-lg font-extrabold text-slate-900">{service.name}</h4>
          {meta.length > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
              {!isProduct && <Clock3 size={14} aria-hidden="true" />}
              {meta.join(" · ")}
            </p>
          )}
          {service.description && (
            <p className="mb-5 mt-3 line-clamp-2 text-sm leading-relaxed text-slate-600">
              {service.description}
            </p>
          )}
          <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <span className="text-sm font-semibold text-slate-700">
              {service.price != null ? money(service.price, service.currency) : "Price on request"}
            </span>
            <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0d5c75] group-hover:underline">
              {isProduct ? "View product" : "View service"}
              <ArrowUpRight size={15} aria-hidden="true" />
            </span>
          </div>
        </div>
      </Link>
    );
  };

  const hasBusinessInfo = services.length > 0 || company.isRemote || company.isVerified || summary.reviewCount > 0 || (company.locations?.length || 0) > 1;
  const share = async () => {
    try {
      if (navigator.share)
        await navigator.share({
          title: company.title,
          text: `View what ${company.title} offers, contact them or make a booking. Follow the business to receive email updates about promotions, new products, services, discounts, sales and specials.`,
          url: window.location.href,
        });
      else {
        await navigator.clipboard.writeText(`View what ${company.title} offers, contact them or make a booking. Follow the business to receive email updates about promotions, new products, services, discounts, sales and specials.\n\n${window.location.origin}/discover/${company.slug || company.encryptedId}`);
        setShareStatus("Profile link copied");
        setTimeout(() => setShareStatus(""), 2500);
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError"))
        setShareStatus("Use your browser address to share this profile");
    }
  };
  const changeImage = (direction: number) =>
    setActiveImage((index) =>
      index === null
        ? null
        : (index + direction + gallery.length) % gallery.length,
    );
  const submitBooking = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setBookingStatus("");
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    const response: any = await AjaxPostAnon("Discover/Booking", {
      encListingId: company.encryptedId,
      encServiceId: data.encServiceId,
      requestedStartLocal: data.requestedStartLocal,
      firstName: data.firstName,
      surname: data.surname,
      email: data.email,
      phone: data.phone,
      message: data.message,
    });
    if (response?.data?.encryptedId) {
      setBookingStatus(`Request sent. Reference: ${response.data.reference}`);
      form.reset();
      track("BookingRequest");
    } else
      setBookingStatus(
        responseMessage(
          response,
          "We could not send your request. Please try again.",
        ),
      );
    setSubmitting(false);
  };
  const submitReview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setReviewStatus("");
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    const response: any = await AjaxPostAnon("Discover/Review", {
      encListingId: company.encryptedId,
      firstName: data.firstName,
      surname: data.surname,
      email: data.email,
      rating: Number(data.rating),
      title: data.title,
      body: data.body,
    });
    if (response?.data?.encryptedId) {
      setReviewStatus("Thank you. Your review is now on this profile.");
      form.reset();
      track("ReviewSubmitted");
    } else
      setReviewStatus(
        responseMessage(
          response,
          "We could not submit your review. Please try again.",
        ),
      );
    setSubmitting(false);
  };

  return (
    <main className={styles.page}>
      <div className={styles.breadcrumbWrap}>
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          <Link href="/">Home</Link>
          <span>/</span>
          <Link href="/discover">Discover Directory</Link>
          <span>/</span>
          <Link href={searchHref({ industry: company.industry })}>
            {company.industry}
          </Link>
          <span>/</span>
          <strong>{company.title}</strong>
        </nav>
      </div>

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.identity}>
            <div className={styles.logoWrap}>
              <div className={styles.logo}>
                {webUrl(company.imageUrl) ? (
                  <img
                    src={webUrl(company.imageUrl)}
                    alt={`${company.title} logo`}
                  />
                ) : (
                  initials(company.title)
                )}
              </div>
              {company.isVerified && (
                <span className={styles.logoVerified} title="Verified business">
                  <BadgeCheck size={19} />
                </span>
              )}
            </div>
            <div className={styles.identityCopy}>
              <div className={styles.topBadges}>
                <Link href={searchHref({ industry: company.industry })}>
                  {company.industry}
                </Link>
                {company.isVerified && (
                  <span className={styles.verifiedPill}>
                    <ShieldCheck size={15} />
                    Verified with Eyam
                    {verificationTags[0]?.name && (
                      <em> · {verificationTags[0].name}</em>
                    )}
                  </span>
                )}
              </div>
              <h1>{company.title}</h1>
              <p className={styles.tagline}>
                {company.description ||
                  `Explore ${company.title}, a ${company.industry} business${company.location ? ` in ${company.location}` : ""}.`}
              </p>
              <div className={styles.meta}>
                {summary.reviewCount > 0 && (
                  <a href="#reviews">
                    <Star size={16} fill="currentColor" />
                    <strong>
                      {Number(summary.averageRating).toFixed(1)}
                    </strong>{" "}
                    ({summary.reviewCount}{" "}
                    {summary.reviewCount === 1 ? "review" : "reviews"})
                  </a>
                )}
                {(company.location || primaryLocation) && (
                  <span>
                    <MapPin size={16} />
                    {company.location ||
                      [primaryLocation?.city, primaryLocation?.province]
                        .filter(Boolean)
                        .join(", ")}
                  </span>
                )}
                {hours.length > 0 && (
                  <span
                    className={company.isOpenNow ? styles.open : styles.closed}
                  >
                    <i />
                    {company.isOpenNow
                      ? `Open now${todayHours?.closeTime ? ` · Closes ${todayHours.closeTime}` : ""}`
                      : "Closed now"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={styles.utilities}>
            <button
              type="button"
              onClick={() => void follow()}
              disabled={followingBusy}
              aria-pressed={saved}
            >
              <Heart size={17} fill={saved ? "currentColor" : "none"} />
              {followingBusy ? "Updating…" : saved ? "Following · Unfollow" : "Follow"}
            </button>
            <button type="button" onClick={share}>
              <Share2 size={17} />
              Share
            </button>
            <button
              type="button"
              aria-label="Report listing"
              title="Report listing"
              onClick={() =>
                (window.location.href = `mailto:support@eyam.finance?subject=${encodeURIComponent(`Report Discover listing: ${company.title}`)}`)
              }
            >
              <Flag size={17} />
            </button>
          </div>
        </div>
        <div className={styles.actionBar}>
          <div className={styles.primaryActions}>
            {bookableServices.length > 0 && (
              <button
                className={styles.primaryButton}
                type="button"
                onClick={() => setBookingOpen(true)}
              >
                <CalendarDays size={19} />
                Book a service
              </button>
            )}
            {whatsappUrl(company.whatsapp) && (
              <a
                className={styles.whatsappButton}
                href={whatsappUrl(company.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("WhatsappReveal")}
              >
                <MessageCircle size={19} />
                WhatsApp direct
              </a>
            )}
            {company.phone && (
              <a
                className={styles.outlineButton}
                href={`tel:${company.phone.replace(/[^\d+]/g, "")}`}
                onClick={() => track("PhoneReveal")}
              >
                <Phone size={18} />
                Call business
              </a>
            )}
            {!bookableServices.length &&
              !company.whatsapp &&
              !company.phone &&
              contactItems.length > 0 && (
                <a className={styles.primaryButton} href="#business-contact">
                  <Mail size={18} />
                  Contact business
                </a>
              )}
          </div>
          <div className={styles.actionNote}>
            {company.isOpenNow ? (
              <>
                <Clock3 size={15} />
                Open and accepting enquiries
              </>
            ) : (
              <>Response times vary by business</>
            )}
          </div>
        </div>
        {shareStatus && (
          <p className={styles.toast} role="status">
            {shareStatus}
          </p>
        )}
      </section>

      <nav className={styles.tabs} aria-label="Business profile sections">
        <div>
          {hasBusinessInfo && <a href="#overview">Services &amp; information</a>}
          {gallery.length > 0 && (
            <a href="#gallery">
              Portfolio <span>{gallery.length}</span>
            </a>
          )}
          {specials.length > 0 && (
            <a href="#specials">
              Specials <span>{specials.length}</span>
            </a>
          )}
          <a href="#reviews">
            Reviews <span>{summary.reviewCount}</span>
          </a>
          {(hours.length > 0 || primaryLocation) && (
            <a href="#location-hours">Location &amp; hours</a>
          )}
        </div>
      </nav>

      <div className={styles.content}>
        <div className={styles.mainColumn}>
          {gallery.length > 0 && (
            <section className={styles.card} id="gallery">
              <div className={styles.sectionHeading}>
                <div>
                  <h2>Portfolio &amp; gallery</h2>
                  <p>A closer look at {company.title}</p>
                </div>
                <button type="button" onClick={() => setActiveImage(0)}>
                  <Photo size={17} />
                  View all {gallery.length} photos
                </button>
              </div>
              <div className={styles.bento}>
                <button
                  className={styles.bentoMain}
                  type="button"
                  onClick={() => setActiveImage(0)}
                >
                  <img
                    src={gallery[0]}
                    alt={`${company.title} portfolio photo 1`}
                  />
                </button>
                {gallery[1] && (
                  <button type="button" onClick={() => setActiveImage(1)}>
                    <img
                      src={gallery[1]}
                      alt={`${company.title} portfolio photo 2`}
                    />
                  </button>
                )}
                {gallery[2] && (
                  <button
                    className={styles.morePhoto}
                    type="button"
                    onClick={() => setActiveImage(2)}
                  >
                    <img
                      src={gallery[2]}
                      alt={`${company.title} portfolio photo 3`}
                    />
                    {gallery.length > 3 && (
                      <span>
                        <Photo size={25} />
                        <strong>+{gallery.length - 2} more</strong>
                        <small>View all photos</small>
                      </span>
                    )}
                  </button>
                )}
              </div>
            </section>
          )}

          {specials.length > 0 && (
            <section id="specials">
              <div className={styles.sectionHeading}>
                <div>
                  <h2>
                    <Tag size={22} />
                    Current specials &amp; promotions
                  </h2>
                  <p>Active offers from this business</p>
                </div>
              </div>
              <div className={styles.specialGrid}>
                {specials.map((special) => {
                  const cta = webUrl(special.callToActionUrl || undefined);
                  return (
                    <article
                      className={styles.specialCard}
                      key={special.encryptedId || special.title}
                    >
                      {special.offerLabel && (
                        <span className={styles.offerLabel}>
                          {special.offerLabel}
                        </span>
                      )}
                      {webUrl(special.imageUrl || undefined) && (
                        <img
                          className={styles.specialImage}
                          src={webUrl(special.imageUrl || undefined)}
                          alt=""
                        />
                      )}
                      <div className={styles.specialBody}>
                        <h3>{special.title}</h3>
                        <p>{special.description}</p>
                        {special.terms && <small>{special.terms}</small>}
                      </div>
                      <div className={styles.specialFooter}>
                        <div>
                          {special.promoCode ? (
                            <>
                              <small>Promo code</small>
                              <strong>{special.promoCode}</strong>
                            </>
                          ) : (
                            <>
                              <small>Offer ends</small>
                              <strong>{dateLabel(special.endsOn)}</strong>
                            </>
                          )}
                        </div>
                        {cta ? (
                          <a
                            href={cta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {special.callToActionLabel || "Claim offer"}
                            <ArrowRight size={15} />
                          </a>
                        ) : bookableServices.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setBookingOpen(true)}
                          >
                            {special.callToActionLabel || "Book now"}
                          </button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {hasBusinessInfo && <section  id="overview" aria-label="Services and business information">
            <div className={styles.featureGrid}>
              {company.isRemote && (
                <div className="!bg-white">
                  <Globe2 />
                  <strong>Remote services</strong>
                  <span>Available beyond the listed location</span>
                </div>
              )}
              {company.isVerified && (
                <div className="!bg-white">
                  <ShieldCheck />
                  <strong>Eyam verified</strong>
                  <span>Trust checks completed</span>
                </div>
              )}
              {summary.reviewCount > 0 && (
                <div className="!bg-white">
                  <Star />
                  <strong>
                    {Number(summary.averageRating).toFixed(1)} rating
                  </strong>
                  <span>From {summary.reviewCount} customers</span>
                </div>
              )}
            </div>
            {serviceOfferings.length > 0 && (
              <div className={`${styles.serviceList} !bg-white`}>
                <div className={`${styles.serviceListHeading} !bg-white`}>
                  <BriefcaseBusiness size={20} />
                  <div>
                    <h3>Services offered</h3>
                    <p>
                      {serviceOfferings.length}{" "}
                      {serviceOfferings.length === 1 ? "service" : "services"} offered
                      {bookableServices.length > 0 &&
                        ` · ${bookableServices.length} bookable online`}
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {serviceOfferings.map((service, index) => renderOffering(service, index))}
                </div>
                {bookableServices.length > 0 && (
                  <button type="button" onClick={() => setBookingOpen(true)}>
                    <CalendarDays size={17} />
                    Request a service
                  </button>
                )}
              </div>
            )}
            {productOfferings.length > 0 && (
              <div className={`${styles.serviceList} !bg-white`}>
                <div className={`${styles.serviceListHeading} !bg-white`}>
                  <Package size={20} />
                  <div>
                    <h3>Products</h3>
                    <p>
                      {productOfferings.length}{" "}
                      {productOfferings.length === 1 ? "product" : "products"} available
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {productOfferings.map((product, index) => renderOffering(product, index))}
                </div>
              </div>
            )}
            {company.locations && company.locations.length > 1 && (
              <div className={styles.serviceAreas}>
                <MapPin size={19} />
                <div>
                  <strong>Business locations</strong>
                  <p>
                    {company.locations
                      .map((location) =>
                        [location.name, location.city, location.province]
                          .filter(Boolean)
                          .join(", "),
                      )
                      .join(" · ")}
                  </p>
                </div>
              </div>
            )}
          </section>

          }
          <section className={styles.card} id="reviews">
            <div className={styles.sectionHeading}>
              <div>
                <h2>Customer reviews &amp; ratings</h2>
                <p>Feedback shared by customers of {company.title}</p>
              </div>
              <button
                className={styles.reviewButton}
                type="button"
                onClick={() => setReviewOpen(true)}
              >
                <MessageCircle size={17} />
                Write a review
              </button>
            </div>
            {summary.reviewCount > 0 ? (
              <>
                <div className={styles.ratingPanel}>
                  <div className={styles.ratingTotal}>
                    <strong>{Number(summary.averageRating).toFixed(1)}</strong>
                    <Stars value={Number(summary.averageRating)} />
                    <span>
                      Based on {summary.reviewCount}{" "}
                      {summary.reviewCount === 1 ? "review" : "reviews"}
                    </span>
                  </div>
                  <div className={styles.ratingBars}>
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = Number(
                        summary.distribution?.[String(star)] || 0,
                      );
                      const percent = summary.reviewCount
                        ? Math.round((count / summary.reviewCount) * 100)
                        : 0;
                      return (
                        <div key={star}>
                          <span>{star} stars</span>
                          <i>
                            <b style={{ width: `${percent}%` }} />
                          </i>
                          <strong>{percent}%</strong>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={styles.reviewList}>
                  {reviews.map((review) => (
                    <article key={review.encryptedId}>
                      <div className={styles.reviewHead}>
                        <div className={styles.avatar}>
                          {initials(review.reviewerName)}
                        </div>
                        <div>
                          <h3>{review.reviewerName}</h3>
                          <p>
                            {dateLabel(review.publishedOn)}
                            {review.isVerifiedClient && (
                              <span>
                                <BadgeCheck size={13} />
                                Verified client
                              </span>
                            )}
                          </p>
                        </div>
                        <Stars value={review.rating} />
                      </div>
                      {review.title && <h4>{review.title}</h4>}
                      <p className={styles.reviewBody}>{review.body}</p>
                      {review.businessResponse && (
                        <div className={styles.ownerResponse}>
                          <strong>
                            <Reply size={15} />
                            Response from {company.title}
                          </strong>
                          <p>{review.businessResponse}</p>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <Star size={34} />
                <h3>Be the first to review this business</h3>
                <p>
                  Share your experience to help other customers make an informed
                  choice.
                </p>
                <button type="button" onClick={() => setReviewOpen(true)}>
                  Write the first review
                </button>
              </div>
            )}
          </section>
        </div>

        <aside className={styles.sidebar}>
          {hours.length > 0 && (
            <section className={styles.sideCard} id="location-hours">
              <div className={styles.sideTitle}>
                <h2>
                  <Clock3 size={20} />
                  Trading hours
                </h2>
                <span
                  className={
                    company.isOpenNow ? styles.openBadge : styles.closedBadge
                  }
                >
                  {company.isOpenNow ? "Open now" : "Closed"}
                </span>
              </div>
              <div className={styles.hours}>
                {hours.map((hour) => (
                  <div
                    className={
                      hour.dayOfWeek === new Date().getDay() ? styles.today : ""
                    }
                    key={`${hour.dayOfWeek}-${hour.displayOrder || 0}`}
                  >
                    <span>{dayNames[hour.dayOfWeek]}</span>
                    <strong>
                      {hour.isClosed
                        ? "Closed"
                        : `${hour.openTime} – ${hour.closeTime}`}
                    </strong>
                  </div>
                ))}
              </div>
              <p className={styles.info}>
                <Info size={15} />
                Booking times must fall within these hours.
              </p>
            </section>
          )}

          {(primaryLocation || company.location) && (
            <section className={styles.sideCard}>
              <h2>
                <MapPin size={20} />
                Business location
              </h2>
              <div className={styles.mapPreview}>
                <MapPin size={34} />
                <span>{primaryLocation?.name || company.location}</span>
                {mapUrl && (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get directions"
                  >
                    <ArrowUpRight size={18} />
                  </a>
                )}
              </div>
              <address>
                {primaryLocation?.name && (
                  <strong>{primaryLocation.name}</strong>
                )}
                <span>{addressText}</span>
              </address>
              {mapUrl && (
                <a
                  className={styles.directions}
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get directions <ArrowUpRight size={14} />
                </a>
              )}
            </section>
          )}

          <section className={styles.sideCard} id="business-contact">
            <h2>Direct contacts</h2>
            {contactItems.length > 0 ? (
              <div className={styles.contacts}>
                {contactItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={
                      item.href?.startsWith("http") ? "_blank" : undefined
                    }
                    rel={
                      item.href?.startsWith("http")
                        ? "noopener noreferrer ugc"
                        : undefined
                    }
                    onClick={() => {
                      if (!usedContacts.current.has(item.label)) {
                        usedContacts.current.add(item.label);
                        track(`${item.label}Reveal`);
                      }
                    }}
                  >
                    <i>
                      <item.icon size={17} />
                    </i>
                    <span>
                      <small>{item.eyebrow}</small>
                      <strong>{item.value}</strong>
                    </span>
                    <ExternalLink size={14} />
                  </a>
                ))}
              </div>
            ) : (
              <p className={styles.info}>
                Contact details are currently unavailable.
              </p>
            )}
            {socials.length > 0 && (
              <div className={styles.socials}>
                <small>Follow this business</small>
                <div>
                  {socials.map((social, index) => (
                    <a
                      key={social.encryptedId || index}
                      href={webUrl(social.url)}
                      target="_blank"
                      rel="noopener noreferrer ugc"
                      title={social.platform}
                      onClick={() =>
                        social.encryptedId &&
                        void AjaxPostAnon("Discover/Social/Analytics", {
                          encListingId: company.encryptedId,
                          encSocialId: social.encryptedId,
                          eventType: "Click",
                          referrerUrl: window.location.href,
                        })
                      }
                    >
                      <Globe2 size={18} />
                      <span>{social.platform}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {isOwner && (
              <Link
                className={styles.manageLink}
                href={`/discover/${company.encryptedId}/admin`}
              >
                Manage this business <ArrowRight size={15} />
              </Link>
            )}
          </section>

          {verificationTags.length > 0 && (
            <section className={styles.trustCard}>
              <h2>
                <ShieldCheck size={24} />
                Why Eyam verified?
              </h2>
              <p>
                Verification tags are awarded after checks by the Eyam team.
              </p>
              <ul>
                {verificationTags.map((tag) => (
                  <li key={tag.encryptedId}>
                    <Check size={16} />
                    <span>
                      <strong>{tag.name}</strong>
                      <small>
                        {tag.publicExplanation ||
                          tag.description ||
                          "Verified by Eyam."}
                      </small>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>

      {activeImage !== null && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={`${company.title} photo gallery`}
          onClick={(event) =>
            event.target === event.currentTarget && setActiveImage(null)
          }
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") changeImage(-1);
            if (event.key === "ArrowRight") changeImage(1);
            if (event.key === "Escape") setActiveImage(null);
          }}
          tabIndex={-1}
        >
          <div className={styles.galleryModal}>
            <div>
              <span>
                Photo {activeImage + 1} of {gallery.length}
              </span>
              <button
                type="button"
                onClick={() => setActiveImage(null)}
                aria-label="Close gallery"
              >
                <X />
              </button>
            </div>
            <img
              src={gallery[activeImage]}
              alt={`${company.title} portfolio photo ${activeImage + 1}`}
            />
            {gallery.length > 1 && (
              <footer>
                <button type="button" onClick={() => changeImage(-1)}>
                  <ChevronLeft />
                  Previous
                </button>
                <button type="button" onClick={() => changeImage(1)}>
                  Next
                  <ChevronRight />
                </button>
              </footer>
            )}
          </div>
        </div>
      )}

      {bookingOpen && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-title"
          onClick={(event) =>
            event.target === event.currentTarget && setBookingOpen(false)
          }
        >
          <div className={styles.formModal}>
            <button
              className={styles.closeModal}
              type="button"
              onClick={() => setBookingOpen(false)}
              aria-label="Close booking form"
            >
              <X />
            </button>
            <p className={styles.eyebrow}>Request an appointment</p>
            <h2 id="booking-title">Book with {company.title}</h2>
            <p>
              Select a service and your preferred time. The business will
              contact you to confirm.
            </p>
            <form onSubmit={submitBooking}>
              <label>
                Service
                <select name="encServiceId" required defaultValue="">
                  <option value="" disabled>
                    Select a service
                  </option>
                  {bookableServices.map((service) => (
                    <option
                      key={service.encryptedId}
                      value={service.encryptedId}
                    >
                      {service.name} · {service.durationMinutes} min
                      {service.price != null
                        ? ` · ${money(service.price, service.currency)}`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Preferred date and time
                <input
                  name="requestedStartLocal"
                  type="datetime-local"
                  required
                  min={new Date(Date.now() + 3600000)
                    .toISOString()
                    .slice(0, 16)}
                />
              </label>
              <div className={styles.formRow}>
                <label>
                  First name
                  <input name="firstName" required maxLength={100} />
                </label>
                <label>
                  Surname
                  <input name="surname" required maxLength={100} />
                </label>
              </div>
              <div className={styles.formRow}>
                <label>
                  Email
                  <input name="email" type="email" required maxLength={256} />
                </label>
                <label>
                  Phone
                  <input name="phone" type="tel" maxLength={50} />
                </label>
              </div>
              <label>
                Message <small>(optional)</small>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={2000}
                  placeholder="Tell the business anything useful about your request"
                />
              </label>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending request…" : "Send booking request"}
                <ArrowRight size={17} />
              </button>
              {bookingStatus && (
                <p className={styles.formStatus} role="status">
                  {bookingStatus}
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {reviewOpen && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-title"
          onClick={(event) =>
            event.target === event.currentTarget && setReviewOpen(false)
          }
        >
          <div className={styles.formModal}>
            <button
              className={styles.closeModal}
              type="button"
              onClick={() => setReviewOpen(false)}
              aria-label="Close review form"
            >
              <X />
            </button>
            <p className={styles.eyebrow}>Share your experience</p>
            <h2 id="review-title">Review {company.title}</h2>
            <p>Your review will appear publicly on this business profile.</p>
            <form onSubmit={submitReview}>
              <label>
                Your rating
                <select name="rating" required defaultValue="5">
                  <option value="5">5 stars · Excellent</option>
                  <option value="4">4 stars · Very good</option>
                  <option value="3">3 stars · Good</option>
                  <option value="2">2 stars · Fair</option>
                  <option value="1">1 star · Poor</option>
                </select>
              </label>
              <div className={styles.formRow}>
                <label>
                  First name
                  <input name="firstName" required maxLength={100} />
                </label>
                <label>
                  Surname
                  <input name="surname" required maxLength={100} />
                </label>
              </div>
              <label>
                Email <small>(not displayed publicly)</small>
                <input name="email" type="email" required maxLength={256} />
              </label>
              <label>
                Review title <small>(optional)</small>
                <input name="title" maxLength={150} />
              </label>
              <label>
                Your review
                <textarea
                  name="body"
                  rows={5}
                  minLength={10}
                  maxLength={3000}
                  required
                  placeholder="Describe the service and your experience"
                />
              </label>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit review"}
                <ArrowRight size={17} />
              </button>
              {reviewStatus && (
                <p className={styles.formStatus} role="status">
                  {reviewStatus}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </main>
  );
}